import { SortAscending, SortDescending } from "@phosphor-icons/react"
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table"
import { useMemo, useState, memo } from "react"
import Loader from "../../../components/Loader"
import Pagination from "../../../components/Pagination"
import Filter from "../../../components/TableFilters"
import { formatISODate } from "../../../utils/datetime"
import ErrorRelatedReportsModal from "./ErrorRelatedReportsModal"
import { logsSource } from "../../../constants"

const columnHelper = createColumnHelper()

const ErrorRelatedReportsTable = memo(
  ({
    data,
    loading,
    onPaginationChange,
    pagination,
    pageCount,
    filters,
    setFilters,
    query,
    isButtonDisabled = false,
  }) => {
    const [isOpen, setIsOpen] = useState(false)
    const [messageId, setMessageId] = useState(false)
    const [ov3Id, setOv3Id] = useState(null)

    const viewReport = (studyUID, patientMRN) => {
      const baseUrl = `https://expertradiology.ramsoftpacs.com/powerreader/mobile/index.aspx`
      const screenWidth = 1000
      const screenHeight = 600
      window.open(
        `${baseUrl}?studyuid=${studyUID}&patientid=${patientMRN}`,
        "newWindow",
        `width=${screenWidth},height=${screenHeight},scrollbars=yes,resizable=yes`
      )
    }

    const showModal = (messageId, ov3Id) => {
      setMessageId(messageId)
      setOv3Id(ov3Id)
      setIsOpen(true)
    }

    const onClose = () => {
      setIsOpen(false)
      setMessageId(null)
      setOv3Id(null)
    }

    const columns = useMemo(
      () => [
        columnHelper.accessor("accessionNumber", {
          header: "Accession Number",
          id: "accessionNumber",
          cell: ({ row }) => {
            if (isButtonDisabled) {
              return row.original.accessionNumber
            }
            return (
              <button
                className="text-blue-500 hover:underline"
                type="button"
                onClick={() =>
                  showModal(row.original.MessageId, row.original.ov3Id)
                }
              >
                {row.original.accessionNumber}
              </button>
            )
          },
          sortDescFirst: false,
          size: 150,
          meta: {
            filterVariant: "text",
          },
        }),
        columnHelper.accessor("occurrenceTime", {
          header: "Occurrence Time",
          id: "occurrenceTime",
          size: 150,
          cell: (info) => formatISODate(info.renderValue()) || "N/A",
          sortDescFirst: false,
          enableColumnFilter: false,
        }),
        columnHelper.accessor("error", {
          header: "Reason",
          id: "error",
          size: 60,
          cell: (info) => info.renderValue() || "N/A",
          sortDescFirst: false,
          enableColumnFilter: false,
        }),
        columnHelper.accessor("Actions", {
          header: "Actions",
          id: "actions",
          size: 120,
          enableSorting: false,
          cell: ({ row }) =>
            row?.original?.["inbound_messages.patientMRN"] &&
            row.original?.["inbound_messages.studyInstanceUid"] && (
              <div className="flex justify-center">
                <button
                  className="bg-secondary hover:bg-secondary_dark px-2 py-1 text-sm text-white rounded"
                  onClick={() => {
                    viewReport(
                      row.original?.["inbound_messages.studyInstanceUid"],
                      row?.original?.["inbound_messages.patientMRN"]
                    )
                  }}
                >
                  View Report
                </button>
              </div>
            ),
          sortDescFirst: false,
          enableColumnFilter: false,
        }),
      ],
      []
    )

    const tableConfig = useMemo(
      () => ({
        columns,
        data,
        manualPagination: true,
        manualSorting: true,
        manualFiltering: true,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        onPaginationChange,
        onColumnFiltersChange: setFilters,
        pageCount,
        state: {
          pagination,
          columnFilters: filters,
        },
      }),
      [
        columns,
        data,
        onPaginationChange,
        setFilters,
        pageCount,
        pagination,
        filters,
      ]
    )

    const tableInstance = useReactTable(tableConfig)

    const rows = useMemo(
      () => tableInstance.getRowModel().rows,
      [tableInstance, data]
    )

    const allColumns = useMemo(
      () => tableInstance.getAllColumns(),
      [tableInstance]
    )

    const TableHeader = useMemo(
      () => (
        <thead>
          {tableInstance.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((column) => (
                <th
                  key={column.id}
                  className="p-2 border-b border-gray-200 bg-gray-100 align-top whitespace"
                  style={{ width: `${column.getSize()}px` }}
                >
                  <div className="flex flex-col justify-start items-start">
                    <button
                      {...{
                        className: column.column.getCanSort()
                          ? "w-full select-none cursor-pointer flex items-center gap-1"
                          : "w-full",
                        onClick: column.column.getToggleSortingHandler(),
                      }}
                    >
                      {flexRender(
                        column.column.columnDef.header,
                        column.getContext()
                      )}
                      {{
                        asc: <SortAscending className="h-4 w-4" />,
                        desc: <SortDescending className="h-4 w-4" />,
                      }[column.column.getIsSorted()] ?? null}
                    </button>
                    {column.column.getCanFilter() ? (
                      <div className="mt-2">
                        <Filter column={column.column} />
                      </div>
                    ) : null}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
      ),
      [tableInstance]
    )

    return (
      <>
        <div className="p-4">
          <table className="min-w-full bg-white border border-gray-200">
            {TableHeader}
            {loading ? (
              <tbody>
                <tr>
                  <td colSpan={allColumns.length}>
                    <div className="py-2">
                      <Loader />
                    </div>
                  </td>
                </tr>
              </tbody>
            ) : (
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-100">
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className="px-4 py-2 border-b border-gray-200"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            )}
          </table>
          {!loading && data.length > 0 && (
            <Pagination tableLib={tableInstance} />
          )}
          {!loading && data.length === 0 && (
            <div className="mt-3 text-center">No Logs found</div>
          )}
          <ErrorRelatedReportsModal
            bodyPart={query.bodyPart}
            isOpen={isOpen}
            messageId={messageId}
            messageType={query.messageType}
            ov3Id={ov3Id}
            source={query.source}
            viewType={query.viewType}
            onClose={onClose}
          />
        </div>
      </>
    )
  }
)

ErrorRelatedReportsTable.displayName = "ErrorRelatedReportsTable"

export default ErrorRelatedReportsTable
